import { useContext, useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { MyUserDataContext } from "../contexts";
import { CustomClaims, RMUser } from "../types";
import { useFunctions } from "./firebaseContext";
import { isRMCertified } from "../../../shared/rmCertification";

export function useRmCertification(email: string | undefined, rm?: Pick<RMUser, "certified">) {
    const userData = useContext(MyUserDataContext);
    const functions = useFunctions();
    const claims = userData.firebaseTokenResult?.claims.customClaims as CustomClaims | undefined;
    const normalizedEmail = email?.trim().toLowerCase();
    const isCurrentUser = !!normalizedEmail && normalizedEmail === userData.user?.email?.toLowerCase();
    const canResolveLocally = isCurrentUser && (claims?.kfh_rm_certified !== undefined || !!rm);
    const [result, setResult] = useState<{ email: string; certified: boolean; error?: boolean }>();

    useEffect(() => {
        if (!normalizedEmail || canResolveLocally) return;
        let active = true;
        httpsCallable<string, boolean>(functions, "getRmCertification")(normalizedEmail)
            .then(({ data }) => {
                if (active) setResult({ email: normalizedEmail, certified: data === true });
            })
            .catch(() => {
                if (active) setResult({ email: normalizedEmail, certified: false, error: true });
            });
        return () => { active = false; };
    }, [functions, normalizedEmail, canResolveLocally]);

    return {
        certified: canResolveLocally ? isRMCertified(claims, rm) : !!result && result.email === normalizedEmail && result.certified,
        loading: !!normalizedEmail && !canResolveLocally && result?.email !== normalizedEmail,
        error: !canResolveLocally && result?.email === normalizedEmail && result?.error === true,
    };
}
